import React, {useState, useEffect, useCallback} from 'react';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {View, Platform, ScrollView, Image, Pressable, KeyboardAvoidingView} from 'react-native';
import {useToast} from 'react-native-fast-toast';
import {StyleSheet} from 'react-native';
import {colorPalette, colors} from '../../../core/config/color.config';

import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {RNEText, RNEButton, RNEInput} from '../../components';
import {
  useAddLaboratoryRequest,
  useGetLaboratoryRequests,
  useUpdateLaboratoryRequest,
} from '../../server/react-query/useMedicalAttachment';
import {MedicalAttachmentText} from '../../server/types/medical-attachment.types';
import {ConsultationStatusEnum} from '../../server/types/consultation.types';
import useStores from '../../stores/useStores';
import {REACT_APP_BASE_WEB_URL} from '../../server/api/api.config';
import LightAppLogo from '../../../assets/logo/logo-light.png';
import {width, height, totalSize} from 'react-native-dimension';
import {Text} from 'react-native';
import {useGetPatientProfile} from '../../server/react-query/usePatientProfile';
import {useGetProfessionalProfile} from '../../server/react-query/usePractitioner';
import moment from 'moment';
import {Overlay} from 'react-native-elements';
import ConsultationCancelConfirmationModal from '../modal/ConsultationCancelConfirmationModal';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';

interface AddLaboratoryRequestViewProps {
  subject: string;
  basedOnId: string;
  userId: string;
}

const schema: yup.Schema<MedicalAttachmentText> = yup.object({
  note: yup.string().trim().required('This field is required.'),
});

const AddLaboratoryRequestView = (props: AddLaboratoryRequestViewProps) => {
  const {authStore} = useStores();
  const navigation = useNavigation();
  const route = useRoute();
  const toast = useToast();
  const {setCanRefresh} = usePullDownRefreshContext();

  const {data, isFetching, refetch} = useGetLaboratoryRequests(props.subject);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: {errors, isValid},
  } = useForm<MedicalAttachmentText>({
    mode: 'all',
    defaultValues: {note: ''},
    resolver: yupResolver(schema),
  });

  const {data: patientData} = useGetPatientProfile({
    patientId: props.subject,
  });

  const {data: practitionerData} = useGetProfessionalProfile();

  const [basedOnId, setBasedOnId] = useState('');
  const [serviceRequestId, setServiceRequestId] = useState('');
  const [subject, setSubject] = useState('');
  const [performer, setPerformer] = useState('');
  const [status, setStatus] = useState<ConsultationStatusEnum>(
    ConsultationStatusEnum.PENDING,
  );

  const [isSaveAttachmentModalVisible, setIsSaveAttachmentModalVisible] =
    useState(false);
  const [confirmationModalTitle, setConfirmationModalTitle] = useState('');
  const [confirmationModalType, setConfirmationModalType] = useState('');

  const addMutation = useAddLaboratoryRequest(
    basedOnId,
    status,
    getValues().note,
    subject,
    performer,
  );

  const updateMutation = useUpdateLaboratoryRequest(
    serviceRequestId,
    {
      note: getValues().note,
      status: status,
    },
    basedOnId,
  );

  const [mutation, setMutation] = useState(addMutation);

  const addMedicalCertificate = (reportStatus: ConsultationStatusEnum) => {
    setStatus(reportStatus);
    handleSubmit(onSubmit)();
  };

  const navProps = {
    procedureId: props.basedOnId,
    subjectId: props.subject,
    receiver: props.subject,
    serviceRequestId: props.basedOnId,
  };

  // REMOVED: useSendMobilePushNotification is no longer used
  // const triggerPushNotification = useSendMobilePushNotification({...});

  const getFullName = humanName => {
    const givenNames = Array.isArray(humanName?.given)
      ? humanName.given.join(' ').trim()
      : '';

    const familyName = humanName?.family ?? '';

    return [givenNames, familyName].filter(Boolean).join(' ');
  };

  const getAgeFromDOB = dobString => {
    const dob = new Date(dobString);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();
    const hasBirthdayPassedThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

    if (!hasBirthdayPassedThisYear) {
      age -= 1;
    }

    return age;
  };

  const getSpecialtiesCommaSeparated = (practitionerRole = []) => {
    return practitionerRole
      .map(role => role?.specialty?.text)
      .filter(Boolean) // remove undefined/null
      .join(', ');
  };

  const onSubmit = async () => {
    if (isValid) {
      try {
        await mutation.mutateAsync();
        refetch();

        reset();
        mutation.reset();
        navigation.goBack();
        // REMOVED: useSendMobilePushNotification is no longer used
        // triggerPushNotification.mutateAsync();
      } catch (error) {
        if (error?.data?.error) {
          toast?.show(error?.data?.error, {type: 'danger'});
        }
      }
    }
  };

  useEffect(() => {
    const params = route.params as any;

    if (params) {
      setBasedOnId(params.basedOnId);
      setSubject(params.subject);
      setPerformer(params.performer);
    }
  }, [route.params]);

  useEffect(() => {
    if (data && data.length > 0) {
      setValue('note', data?.[0]?.note[0].text, {shouldValidate: true});
      setServiceRequestId(data?.[0]?.serviceRequestId);
      setMutation(updateMutation);
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (props.subject) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <>
      {Platform.OS === 'web' ? (
        <ScrollView>
          <View>
            <View style={webStyles.formContainer}>
              <View style={webStyles.rightContainer}>
                <RNEText
                  label="Laboratory Certificate"
                  style={subtitleStyle}
                  variant="title"
                  weight="bold"
                />
                <View style={webStyles.textareaContainer}>
                  <Controller
                    control={control}
                    render={({field: {onChange, onBlur, value}}) => (
                      <RNEInput
                        placeholder="Laboratory Certificate"
                        variant="shadowed"
                        multiline={true}
                        height={320}
                        inputContainerStyle={inputContainerWebStyle}
                        inputStyle={textareaWebStyle}
                        value={value || ''}
                        onChangeText={text => {
                          onChange(text);
                        }}
                        errorMessage={errors.note?.message}
                      />
                    )}
                    name="note"
                  />
                </View>
                <View style={webStyles.buttonsContainer}>
                  <RNEButton
                    title="Cancel"
                    color="accent"
                    style={webStyles.buttonStyle}
                    variant="outlined"
                    height={60}
                    onPress={navigation.goBack}
                  />
                  <View style={webStyles.rightButtonsContainer}>
                    <View style={webStyles.buttonContainer}>
                      <RNEButton
                        title="Save Draft"
                        color="secondary"
                        style={webStyles.buttonStyle}
                        variant="outlined"
                        height={60}
                        loadingProps={loadingStyle}
                        loading={
                          (addMutation.isLoading || updateMutation.isLoading) &&
                          status === ConsultationStatusEnum.PENDING
                        }
                        onPress={() =>
                          addMedicalCertificate(ConsultationStatusEnum.PENDING)
                        }
                      />
                    </View>
                    <RNEButton
                      title="Submit"
                      color="secondary"
                      style={webStyles.buttonStyle}
                      height={60}
                      loading={
                        (addMutation.isLoading || updateMutation.isLoading) &&
                        status === ConsultationStatusEnum.COMPLETED
                      }
                      onPress={() =>
                        addMedicalCertificate(ConsultationStatusEnum.COMPLETED)
                      }
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <KeyboardAvoidingView
          style={{flex: 1}}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0} // adjust based on your header height
        >
          <ScrollView
            onScroll={handleScroll}
            onLayout={e => {
              setScrollViewHeight(e.nativeEvent.layout.height);
            }}
            onContentSizeChange={(contentWidth, contentHeight) => {
              setContentHeight(contentHeight);
            }}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: height(5),
            }}>
            <View style={mobileStyles.mainContainer}>
              {/* Logo */}
              <View>
                <Image
                  source={LightAppLogo}
                  style={{width: width(30), height: width(10), opacity: 0.3}}
                  resizeMode="contain"
                />
              </View>

              {/* Body */}
              <View style={{padding: height(2), paddingTop: 0}}>
                <View
                  style={{
                    borderBottomColor: colors.black.transparent23,
                    borderBottomWidth: width(0.4),
                    paddingBottom: height(1.5),
                  }}>
                  <Text
                    style={{
                      fontSize: totalSize(2.2),
                      color: colors.midnightBlue.regular,
                      fontFamily: 'Quicksand-Bold',
                    }}>
                    Laboratory Certificate
                  </Text>
                </View>

                <View
                  style={{
                    marginTop: height(2),
                    borderBottomColor: colors.black.transparent23,
                    borderBottomWidth: width(0.4),
                    paddingBottom: height(1.5),
                  }}>
                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        textAlign: 'right',
                        width: width(20),
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Name:
                    </Text>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {patientData?.humanName
                        ? getFullName(patientData?.humanName)
                        : '-'}
                    </Text>
                  </View>

                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        textAlign: 'right',
                        width: width(20),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Age:
                    </Text>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {patientData?.birthdate
                        ? getAgeFromDOB(patientData?.birthdate)
                        : '-'}
                    </Text>
                  </View>

                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        textAlign: 'right',
                        width: width(20),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Address:
                    </Text>
                    <Text
                      style={{
                        flexWrap: 'wrap',
                        width: width(60),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {patientData?.location ? patientData?.location : '-'}
                    </Text>
                  </View>
                </View>

                <View style={{marginTop: height(2), marginBottom: height(2.5)}}>
                  <Controller
                    control={control}
                    render={({field: {onChange, onBlur, value}}) => (
                      <RNEInput
                        height={300}
                        placeholder="Laboratory Certificate"
                        multiline={true}
                        textAlignVertical="top"
                        inputContainerStyle={inputContainerMobileStyle}
                        inputStyle={inputStyle}
                        value={value || ''}
                        onChangeText={text => {
                          onChange(text);
                        }}
                        errorMessage={errors.note?.message}
                      />
                    )}
                    name="note"
                  />
                </View>

                {/* Signature */}
                <View>
                  {practitionerData?.signature && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        width: width(100),
                        marginBottom: -height(4),
                      }}>
                      <Image
                        style={{
                          width: width(45),
                          height: width(20),
                          marginRight: width(10),
                        }}
                        resizeMode="cover"
                        resizeMethod="resize"
                        source={{uri: practitionerData?.signature}}
                      />
                    </View>
                  )}
                  <View>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.8),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      {practitionerData?.humanName
                        ? getFullName(practitionerData?.humanName)
                        : '-'}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      {practitionerData?.practitionerRole
                        ? getSpecialtiesCommaSeparated(
                            practitionerData?.practitionerRole,
                          )
                        : '-'}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      License No:{' '}
                      {practitionerData?.qualification?.identifier || '-'}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      Issued On: {moment().local().format('MMM DD, YYYY')}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Footer */}
              <View style={mobileStyles.buttonsContainer}>
                {/* <RNEButton
                title="Cancel"
                color="accent"
                variant="outlined"
                onPress={navigation.goBack}
              /> */}
                <View style={mobileStyles.rightButtonsContainer}>
                  <View style={mobileStyles.buttonContainer}>
                    <Pressable
                      onPress={() => {
                        if (isValid) {
                          setConfirmationModalTitle(
                            'Are you sure you want to save this as a draft?',
                          );
                          setConfirmationModalType(
                            ConsultationStatusEnum.PENDING,
                          );
                          setIsSaveAttachmentModalVisible(
                            !isSaveAttachmentModalVisible,
                          );
                        } else {
                          addMedicalCertificate(ConsultationStatusEnum.PENDING);
                        }
                      }}>
                      <View
                        style={{
                          padding: totalSize(1),
                          paddingRight: totalSize(3),
                          paddingLeft: totalSize(3),
                          borderColor: colorPalette.secondary,
                          borderWidth: totalSize(0.2),
                          borderRadius: totalSize(6),
                        }}>
                        <Text
                          style={{
                            color: colorPalette.secondary,
                            fontSize: totalSize(1.8),
                            fontFamily: 'Quicksand-SemiBold',
                          }}>
                          Save Draft
                        </Text>
                      </View>
                    </Pressable>
                    {/* <RNEButton
                    title="Save Draft"
                    color="secondary"
                    variant="outlined"
                    style={{
                      paddingLeft: width(6),
                      paddingRight: width(6),
                      height: height(5),
                    }}
                    titleStyle={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingTop: height(0),
                      lineHeight: height(1.8),
                    }}
                    loadingProps={loadingStyle}
                    // loading={
                    //   (addMutation.isLoading || updateMutation.isLoading) &&
                    //   status === ConsultationStatusEnum.PENDING
                    // }
                    onPress={() => {
                      if (isValid) {
                        setConfirmationModalTitle(
                          'Are you sure you want to save this as a draft?',
                        );
                        setConfirmationModalType(
                          ConsultationStatusEnum.PENDING,
                        );
                        setIsSaveAttachmentModalVisible(
                          !isSaveAttachmentModalVisible,
                        );
                      } else {
                        addMedicalCertificate(ConsultationStatusEnum.PENDING);
                      }
                    }}
                  /> */}
                  </View>

                  <Pressable
                    onPress={() => {
                      if (isValid) {
                        setConfirmationModalTitle(
                          'Are you sure you want to send this to the patient?',
                        );
                        setConfirmationModalType(
                          ConsultationStatusEnum.COMPLETED,
                        );
                        setIsSaveAttachmentModalVisible(
                          !isSaveAttachmentModalVisible,
                        );
                      } else {
                        addMedicalCertificate(ConsultationStatusEnum.COMPLETED);
                      }
                    }}>
                    <View
                      style={{
                        padding: totalSize(1),
                        paddingRight: totalSize(4.5),
                        paddingLeft: totalSize(4.5),
                        backgroundColor: colorPalette.secondary,
                        borderColor: colorPalette.secondary,
                        borderWidth: totalSize(0.3),
                        borderRadius: totalSize(6),
                      }}>
                      <Text
                        style={{
                          color: colorPalette.white,
                          fontSize: totalSize(1.8),
                          fontFamily: 'Quicksand-SemiBold',
                        }}>
                        Submit
                      </Text>
                    </View>
                  </Pressable>
                  {/* <RNEButton
                  title="Submit"
                  color="secondary"
                  buttonBorderWidth={2}
                  style={{
                    paddingLeft: width(8),
                    paddingRight: width(8),
                    height: height(5),
                  }}
                  titleStyle={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: height(0),
                    lineHeight: height(1.8),
                  }}
                  // loading={
                  //   (addMutation.isLoading || updateMutation.isLoading) &&
                  //   status === ConsultationStatusEnum.COMPLETED
                  // }
                  onPress={() => {
                    if (isValid) {
                      setConfirmationModalTitle(
                        'Are you sure you want to send this to the patient?',
                      );
                      setConfirmationModalType(
                        ConsultationStatusEnum.COMPLETED,
                      );
                      setIsSaveAttachmentModalVisible(
                        !isSaveAttachmentModalVisible,
                      );
                    } else {
                      addMedicalCertificate(ConsultationStatusEnum.COMPLETED);
                    }
                  }}
                /> */}
                </View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      )}

      <Overlay
        isVisible={isSaveAttachmentModalVisible}
        overlayStyle={sharedStyles.overlayContainer}>
        <ConsultationCancelConfirmationModal
          title={confirmationModalTitle}
          textStyle={{fontSize: totalSize(2)}}
          primaryBtnText={'Yes'}
          secondaryBtnText={'No'}
          isLoading={addMutation.isLoading || updateMutation.isLoading}
          onConfirm={() => {
            if (confirmationModalType === ConsultationStatusEnum.PENDING) {
              addMedicalCertificate(ConsultationStatusEnum.PENDING);
            } else if (
              confirmationModalType === ConsultationStatusEnum.COMPLETED
            ) {
              addMedicalCertificate(ConsultationStatusEnum.COMPLETED);
            }
          }}
          onCancel={() =>
            setIsSaveAttachmentModalVisible(!isSaveAttachmentModalVisible)
          }
        />
      </Overlay>
    </>
  );
};

export default AddLaboratoryRequestView;

const subtitleStyle = {
  fontSize: 16,
  paddingLeft: 20,
};

const inputContainerWebStyle = {
  borderRadius: 20,
};

const textareaWebStyle = {
  padding: 20,
  lineHeight: 24,
};

const inputContainerMobileStyle = {
  borderRadius: totalSize(2.5),
  paddingTop: height(1),
  paddingBottom: height(2),
};

const inputStyle = {
  lineHeight: height(2),
};

const loadingStyle = {
  color: colorPalette.secondary,
};

const webStyles = StyleSheet.create({
  titleContainer: {
    flexDirection: 'row',
    marginHorizontal: 100,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: colorPalette.grey,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 100,
    paddingRight: 100,
    paddingVertical: 60,
  },
  formContainer: {
    backgroundColor: colorPalette.white,
    borderRadius: 20,
    paddingHorizontal: 12,
    marginBottom: 80,
    flexDirection: 'row',
  },
  leftContainer: {
    flexShrink: 0,
    borderRightColor: 'rgba(229, 229, 229, 0.7)',
    borderRightWidth: 1,
    paddingVertical: 60,
    paddingRight: 32,
  },
  subtitleContainer: {
    marginBottom: 40,
    paddingLeft: 12,
  },
  infoContainer: {
    marginBottom: 44,
  },
  inputLabel: {
    paddingLeft: 24,
    marginBottom: 4,
  },
  rightContainer: {
    flex: 3,
  },
  textareaContainer: {
    marginTop: 52,
  },
  buttonContainer: {
    marginRight: 20,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 32,
    marginLeft: 20,
    flexWrap: 'wrap',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    marginLeft: 20,
    flexWrap: 'wrap',
  },
  buttonStyle: {
    width: 140,
  },
});

const mobileStyles = StyleSheet.create({
  mainContainer: {
    width: '100%',
    // flex: 1,
    // height: height(95),
    paddingHorizontal: width(5),
    // paddingBottom: 52,
    paddingTop: height(1),
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconContainer: {
    paddingBottom: 12,
  },
  bodyContainer: {
    paddingVertical: 40,
  },
  buttonContainer: {
    marginRight: width(4),
  },
  buttonsContainer: {
    // position: 'absolute',
    marginTop: height(2),
    // left: 0,
    // right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    // marginLeft: 20,
    flexWrap: 'wrap',
  },
});

const sharedStyles = StyleSheet.create({
  overlayContainer: {
    borderRadius: 20,
    marginHorizontal: 40,
  },
});
